import { useCallback, useEffect, useRef, useState } from 'react';
import { cooperplaceApi } from 'services/api';
import { KanbanContractError } from '../contract';
import { CANDIDATES_PER_PAGE, validateCandidates } from './contract';
import { assertAppliedFilters } from '../Filters/contract';

export default function useCandidates({ context, enabled, radiusKm, status, page }) {
  const [state, setState] = useState({ data: null, loading: enabled, error: null });
  const [revision, setRevision] = useState(0);
  const generation = useRef(0);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  const { companyId, item } = context;
  // Ponto de integração da 5289. Nunca remover silenciosamente filtros do contexto.
  const filtersKey = JSON.stringify(context.query?.filters ?? null);
  const queryKey = JSON.stringify([
    companyId,
    item.loadId,
    item.type,
    filtersKey,
    radiusKm,
    status,
    page,
    revision,
    enabled,
  ]);
  useEffect(() => {
    const current = ++generation.current;
    const abort = new AbortController();
    setState({ queryKey, data: null, loading: enabled, error: null });
    if (!enabled) return () => abort.abort();
    const params = { radiusKm, status, page, perPage: CANDIDATES_PER_PAGE };
    const filters = JSON.parse(filtersKey);
    if (filters !== null) params.filters = JSON.stringify(filters);
    const timer = setTimeout(() => {
      cooperplaceApi
        .get(`control-tower/loads/${item.loadId}/candidates`, { params, signal: abort.signal })
        .then(({ data }) => {
          if (abort.signal.aborted || current !== generation.current) return;
          assertAppliedFilters(data, filters);
          setState({
            queryKey,
            data: validateCandidates(data, { loadId: item.loadId, type: item.type }, params),
            loading: false,
            error: null,
          });
        })
        .catch((error) => {
          if (abort.signal.aborted || current !== generation.current) return;
          const code = error.response?.status;
          const message = [401, 403].includes(code)
            ? 'Você não tem permissão para consultar os veículos desta carga.'
            : code === 404
              ? 'Carga não encontrada ou indisponível para consulta.'
              : code === 409
                ? // O motivo do backend diz se a carga saiu da fila (atendida, vencida) ou só mudou.
                  error.response?.data?.message ||
                  'A carga ou os candidatos foram alterados. Atualize a consulta.'
                : code === 422
                  ? 'Não foi possível aplicar os filtros de veículos. Revise a consulta.'
                  : code === 503
                    ? 'Não foi possível confirmar os candidatos para os filtros. Atualize ou revise a consulta.'
                    : error instanceof KanbanContractError
                      ? error.message
                      : 'Não foi possível consultar os veículos. Tente novamente.';
          setState({ queryKey, data: null, loading: false, error: message, errorCode: code });
        });
    }, 200);
    return () => {
      clearTimeout(timer);
      abort.abort();
    };
  }, [companyId, item.loadId, item.type, filtersKey, radiusKm, status, page, enabled, queryKey]);
  const visible =
    state.queryKey === queryKey ? state : { data: null, loading: enabled, error: null };
  return { ...visible, refresh, queryKey };
}
